import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from './supabase';

export type Currency = 'USD' | 'MXN';

type FxRow = {
  from_currency: string;
  to_currency: string;
  rate: number;
  source: string;
  source_date: string | null;
  fetched_at: string;
};

type Ctx = {
  // TC vigente USD→MXN
  usdMxn: number | null;
  source: string | null;
  fetchedAt: string | null;
  loading: boolean;
  // moneda preferida del visitante
  preferred: Currency;
  setPreferred: (c: Currency) => void;
  // helpers
  fmtUsd: (n: number | null | undefined) => string;
  fmtMxn: (n: number | null | undefined) => string;
  /**
   * Toma un precio en USD y devuelve display en la moneda preferida.
   * Si el precio nativo viene en MXN (legacy), pasa nativeCurrency='MXN'.
   */
  display: (
    priceUsd: number | null | undefined,
    priceMxn?: number | null | undefined,
  ) => { primary: string; secondary: string | null };
};

const C = createContext<Ctx | null>(null);

const LS_KEY = 'nuled_currency_pref';

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [usdMxn, setUsdMxn] = useState<number | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [preferred, setPreferredState] = useState<Currency>(() => {
    if (typeof window === 'undefined') return 'USD';
    return (localStorage.getItem(LS_KEY) as Currency) || 'USD';
  });

  useEffect(() => {
    let alive = true;
    supabase
      .from('fx_latest')
      .select('*')
      .eq('from_currency', 'USD')
      .eq('to_currency', 'MXN')
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return;
        const row = data as FxRow | null;
        if (row) {
          setUsdMxn(Number(row.rate));
          setSource(row.source);
          setFetchedAt(row.fetched_at);
        }
        setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const setPreferred = (c: Currency) => {
    setPreferredState(c);
    if (typeof window !== 'undefined') localStorage.setItem(LS_KEY, c);
  };

  const fmtUsd = (n: number | null | undefined) =>
    n == null ? '—' : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} USD`;

  const fmtMxn = (n: number | null | undefined) =>
    n == null ? '—' : `$${Number(n).toLocaleString('es-MX', { maximumFractionDigits: 0 })} MXN`;

  const display = (priceUsd: number | null | undefined, priceMxn?: number | null | undefined) => {
    const tc = usdMxn ?? 18;
    if (priceUsd != null && Number(priceUsd) > 0) {
      const usd = Number(priceUsd);
      const mxn = usd * tc;
      return preferred === 'USD'
        ? { primary: fmtUsd(usd), secondary: fmtMxn(mxn) }
        : { primary: fmtMxn(mxn),  secondary: fmtUsd(usd) };
    }
    if (priceMxn != null && Number(priceMxn) > 0) {
      // legacy: solo tenemos MXN
      const mxn = Number(priceMxn);
      const usd = mxn / tc;
      return preferred === 'USD'
        ? { primary: fmtUsd(usd), secondary: fmtMxn(mxn) + ' (legacy)' }
        : { primary: fmtMxn(mxn),  secondary: null };
    }
    return { primary: '—', secondary: null };
  };

  return (
    <C.Provider value={{
      usdMxn, source, fetchedAt, loading,
      preferred, setPreferred,
      fmtUsd, fmtMxn, display,
    }}>
      {children}
    </C.Provider>
  );
}

export function useCurrency() {
  const v = useContext(C);
  if (!v) throw new Error('useCurrency fuera de CurrencyProvider');
  return v;
}
